import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { rpcClient } from 'src/api/rpc/client';
import type {
  ListWorkflowDefinitionSummariesResponse,
  WorkflowDefinitionResponse,
  ListWorkflowRunsForDefinitionResponse,
  WorkflowRunResponse,
  CreateWorkflowDefinitionRequest,
  UpdateWorkflowDefinitionRequest,
  ListStepTypesResponse,
  StepRunResponse,
  RetryStepResponse,
  RestartFromStepResponse,
} from '@erp/rpc';

/**
 * List workflow definition summaries (with node count and last run)
 */
export function useListWorkflowDefinitionSummaries(params?: {
  limit?: number;
  offset?: number;
}) {
  return useQuery<ListWorkflowDefinitionSummariesResponse>({
    queryKey: ['workflow-definitions', 'summaries', params],
    queryFn: async () => {
      return await rpcClient.workflows.listDefinitionSummaries({
        limit: params?.limit,
        offset: params?.offset,
      });
    },
  });
}

/**
 * Get a workflow definition by ID
 */
export function useGetWorkflowDefinition(workflowDefinitionId?: string) {
  return useQuery<WorkflowDefinitionResponse>({
    queryKey: ['workflow-definitions', workflowDefinitionId],
    queryFn: async () => {
      if (!workflowDefinitionId) {
        throw new Error('Workflow definition ID is required');
      }
      return await rpcClient.workflows.getDefinition({
        workflowDefinitionId,
      });
    },
    enabled: !!workflowDefinitionId,
  });
}

/**
 * List runs for a workflow definition
 */
export function useListWorkflowRunsForDefinition(
  workflowDefinitionId?: string,
  params?: {
    limit?: number;
    offset?: number;
  },
) {
  return useQuery<ListWorkflowRunsForDefinitionResponse>({
    queryKey: ['workflow-runs', workflowDefinitionId, params],
    queryFn: async () => {
      if (!workflowDefinitionId) {
        throw new Error('Workflow definition ID is required');
      }
      return await rpcClient.workflows.listRunsForDefinition({
        workflowDefinitionId,
        limit: params?.limit,
        offset: params?.offset,
      });
    },
    enabled: !!workflowDefinitionId,
  });
}

/**
 * Get a workflow run by ID
 */
export function useGetWorkflowRun(workflowRunId?: string) {
  return useQuery<WorkflowRunResponse>({
    queryKey: ['workflow-runs', 'detail', workflowRunId],
    queryFn: async () => {
      if (!workflowRunId) {
        throw new Error('Workflow run ID is required');
      }
      return await rpcClient.workflows.getRun({
        workflowRunId,
      });
    },
    enabled: !!workflowRunId,
  });
}

/**
 * Create a new workflow definition
 */
export function useCreateWorkflowDefinition() {
  const queryClient = useQueryClient();

  return useMutation<
    WorkflowDefinitionResponse,
    Error,
    CreateWorkflowDefinitionRequest
  >({
    mutationFn: async (input) => {
      return await rpcClient.workflows.createDefinition(input);
    },
    onSuccess: () => {
      // Invalidate the summaries list to show the new workflow
      queryClient.invalidateQueries({
        queryKey: ['workflow-definitions', 'summaries'],
      });
    },
  });
}

/**
 * Update a workflow definition
 */
export function useUpdateWorkflowDefinition() {
  const queryClient = useQueryClient();

  return useMutation<
    WorkflowDefinitionResponse,
    Error,
    UpdateWorkflowDefinitionRequest
  >({
    mutationFn: async (input) => {
      return await rpcClient.workflows.updateDefinition(input);
    },
    onSuccess: (data) => {
      // Invalidate and refetch the specific definition
      queryClient.invalidateQueries({
        queryKey: ['workflow-definitions', data.id],
      });
      // Also invalidate the summaries list
      queryClient.invalidateQueries({
        queryKey: ['workflow-definitions', 'summaries'],
      });
    },
  });
}

/**
 * List available step types for workflow definitions
 */
export function useListStepTypes() {
  return useQuery<ListStepTypesResponse>({
    queryKey: ['workflow-step-types'],
    queryFn: async () => {
      return await rpcClient.workflows.listStepTypes({});
    },
    staleTime: 1000 * 60 * 10, // Step types rarely change, cache for 10 minutes
  });
}

/**
 * Get step runs for a workflow run
 */
export function useGetStepRuns(workflowRunId?: string) {
  return useQuery<{ stepRuns: StepRunResponse[] }>({
    queryKey: ['workflow-step-runs', workflowRunId],
    queryFn: async () => {
      if (!workflowRunId) {
        throw new Error('Workflow run ID is required');
      }
      return await rpcClient.workflows.getStepRuns({
        workflowRunId,
      });
    },
    enabled: !!workflowRunId,
  });
}

/**
 * Get child workflow runs for a parent workflow run
 */
export function useGetChildWorkflowRuns(parentWorkflowRunId?: string) {
  return useQuery<{ childWorkflowRuns: WorkflowRunResponse[] }>({
    queryKey: ['workflow-child-runs', parentWorkflowRunId],
    queryFn: async () => {
      if (!parentWorkflowRunId) {
        throw new Error('Parent workflow run ID is required');
      }
      return await rpcClient.workflows.getChildWorkflowRuns({
        parentWorkflowRunId,
      });
    },
    enabled: !!parentWorkflowRunId,
  });
}

/**
 * Retry a failed or timed_out step
 */
export function useRetryStep() {
  const queryClient = useQueryClient();

  return useMutation<
    RetryStepResponse,
    Error,
    {
      workflowRunId: string;
      stepRunId: string;
      inputData?: Record<string, unknown>;
    }
  >({
    mutationFn: async ({ workflowRunId, stepRunId, inputData }) => {
      return await rpcClient.workflows.retryStep({
        workflowRunId,
        stepRunId,
        inputData,
      });
    },
    onSuccess: (data) => {
      // Invalidate the step runs and workflow run
      queryClient.invalidateQueries({
        queryKey: ['workflow-step-runs', data.workflowRun.id],
      });
      queryClient.invalidateQueries({
        queryKey: ['workflow-runs', 'detail', data.workflowRun.id],
      });
      queryClient.invalidateQueries({
        queryKey: ['workflow-runs'],
      });
    },
  });
}

/**
 * Restart workflow from a specific step
 */
export function useRestartFromStep() {
  const queryClient = useQueryClient();

  return useMutation<
    RestartFromStepResponse,
    Error,
    {
      workflowRunId: string;
      workflowStepId: string;
      inputData?: Record<string, unknown>;
    }
  >({
    mutationFn: async ({ workflowRunId, workflowStepId, inputData }) => {
      return await rpcClient.workflows.restartFromStep({
        workflowRunId,
        workflowStepId,
        inputData,
      });
    },
    onSuccess: (data) => {
      // Invalidate the step runs and workflow run
      queryClient.invalidateQueries({
        queryKey: ['workflow-step-runs', data.workflowRun.id],
      });
      queryClient.invalidateQueries({
        queryKey: ['workflow-runs', 'detail', data.workflowRun.id],
      });
      queryClient.invalidateQueries({
        queryKey: ['workflow-runs'],
      });
    },
  });
}

/**
 * Duplicate a workflow definition
 */
export function useDuplicateWorkflowDefinition() {
  const queryClient = useQueryClient();

  return useMutation<
    WorkflowDefinitionResponse,
    Error,
    { workflowDefinitionId: string; name: string }
  >({
    mutationFn: async (input) => {
      return await rpcClient.workflows.duplicateDefinition(input);
    },
    onSuccess: () => {
      // Invalidate the summaries list to show the new workflow
      queryClient.invalidateQueries({
        queryKey: ['workflow-definitions', 'summaries'],
      });
    },
  });
}
